import React from 'react';
import styled from 'styled-components';
import { FaCheckCircle } from 'react-icons/fa';
import Reveal from './ui/Reveal';
import SpotlightCard from './ui/SpotlightCard';
import CodeTerminal from './ui/CodeTerminal';
import { useI18n } from '../context/LanguageContext';

const Section = styled.section`
  max-width: var(--max-width);
  margin: 0 auto;
  padding: 6rem 2rem;
  @media (max-width: 768px) { padding: 4.5rem 1.25rem; text-align: center; }
`;

const Head = styled.div`
  margin-bottom: 3rem; max-width: 640px;
  h2 { font-size: clamp(2rem, 4.5vw, 2.9rem); margin-bottom: 1rem; }
  p { color: var(--text-muted); font-size: 1.05rem; }
  @media (max-width: 768px) { margin: 0 auto 2.5rem; }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1.15fr 0.85fr;
  gap: 1.75rem;
  @media (max-width: 900px) { grid-template-columns: 1fr; }
`;

const Bio = styled(SpotlightCard)`
  padding: 2rem;
  @media (max-width: 768px) { text-align: left; }
  p { color: var(--text-muted); margin-bottom: 1.1rem; line-height: 1.8; }
  strong { color: var(--text); font-weight: 600; }
  .hl { color: var(--secondary); font-weight: 600; }
`;

const Highlights = styled.ul`
  list-style: none;
  margin-top: 1.5rem;
  display: grid;
  gap: 0.75rem;
  li {
    display: flex; align-items: flex-start; gap: 0.65rem;
    color: var(--text); font-size: 0.96rem;
  }
  svg { color: var(--primary-light); flex-shrink: 0; margin-top: 3px; }
`;

const Side = styled.div`
  display: flex; flex-direction: column; gap: 1.75rem;
`;

const Card = styled(SpotlightCard)`
  padding: 1.75rem;
  @media (max-width: 768px) { text-align: left; }
  h4 {
    font-family: var(--font-body); font-size: 0.78rem; font-weight: 600;
    letter-spacing: 0.14em; text-transform: uppercase; color: var(--text-faint);
    margin-bottom: 1.1rem;
  }
`;

const Lang = styled.div`
  display: flex; align-items: center; justify-content: space-between;
  padding: 0.7rem 0; border-bottom: 1px solid var(--border);
  &:last-child { border-bottom: none; }
  .name { font-weight: 600; }
  .level {
    font-size: 0.8rem; color: var(--primary-light);
    padding: 0.2rem 0.7rem; border-radius: 999px;
    border: 1px solid var(--border); background: var(--bg-alt);
  }
`;

const Now = styled.div`
  .role { font-family: var(--font-heading); font-weight: 600; font-size: 1.05rem; }
  .org { color: var(--secondary); font-weight: 600; font-size: 0.92rem; margin-top: 0.2rem; }
  .desc { color: var(--text-muted); font-size: 0.92rem; margin-top: 0.6rem; line-height: 1.6; }
`;

const bios = {
  en: (
    <>
      <p>
        I'm a <strong>Full-Stack Developer</strong> based in <span className="hl">Marseille</span>,
        with a <strong>Master's in Software Engineering</strong> and close to{' '}
        <span className="hl">2 years</span> of professional experience building web & mobile
        applications in Agile environments.
      </p>
      <p>
        I work across the <span className="hl">Java, React and PHP</span> ecosystem, with a double
        competency in mobile (<span className="hl">Flutter</span>) and DevOps
        (<span className="hl">Docker, CI/CD</span>). Today I build internal tools used daily by
        ~350 agents at <strong>CCAS de Marseille</strong>; before that I delivered an enterprise
        web & mobile app for the <strong>Grand Port Maritime de Marseille</strong> at ATOS.
      </p>
      <p>
        I'm currently looking for a <span className="hl">Full-Stack Developer role (CDI)</span> where
        I can keep shipping software people rely on.
      </p>
    </>
  ),
  fr: (
    <>
      <p>
        Je suis <strong>Développeur Full-Stack</strong> basé à <span className="hl">Marseille</span>,
        titulaire d'un <strong>Master en génie logiciel</strong> avec près de{' '}
        <span className="hl">2 ans</span> d'expérience professionnelle dans le développement
        d'applications web & mobiles en environnement Agile.
      </p>
      <p>
        Je travaille dans l'écosystème <span className="hl">Java, React et PHP</span>, avec une
        double compétence mobile (<span className="hl">Flutter</span>) et DevOps
        (<span className="hl">Docker, CI/CD</span>). Aujourd'hui je développe des outils internes
        utilisés chaque jour par ~350 agents au <strong>CCAS de Marseille</strong> ; auparavant,
        j'ai livré une application web & mobile d'entreprise pour le{' '}
        <strong>Grand Port Maritime de Marseille</strong> chez ATOS.
      </p>
      <p>
        Je recherche actuellement un poste de <span className="hl">Développeur Full-Stack (CDI)</span>{' '}
        où je pourrai continuer à livrer des logiciels sur lesquels on peut compter.
      </p>
    </>
  ),
};

const About = () => {
  const { t, data, lang } = useI18n();
  const { profile } = data;

  return (
  <Section id="about">
    <Reveal>
      <Head>
        <span className="eyebrow">{t.about.eyebrow}</span>
        <h2>{t.about.h2a}<span className="gradient-text">{t.about.h2b}</span></h2>
        <p>{t.about.sub}</p>
      </Head>
    </Reveal>

    <Grid>
      <Reveal>
        <Bio>
          {bios[lang]}
          <Highlights>
            {t.about.highlights.map((h) => (
              <li key={h}><FaCheckCircle /> {h}</li>
            ))}
          </Highlights>
        </Bio>
      </Reveal>

      <Side>
        <Reveal delay={0.03}>
          <CodeTerminal />
        </Reveal>
        <Reveal delay={0.05}>
          <Card>
            <h4>{t.about.currently}</h4>
            <Now>
              <div className="role">{t.about.currentlyRole}</div>
              <div className="org">CCAS de Marseille</div>
              <div className="desc">{t.about.currentlyDesc}</div>
            </Now>
          </Card>
        </Reveal>
        <Reveal delay={0.1}>
          <Card>
            <h4>{t.about.languages}</h4>
            {profile.spokenLanguages.map((l) => (
              <Lang key={l.name}>
                <span className="name">{l.name}</span>
                <span className="level">{l.level}</span>
              </Lang>
            ))}
          </Card>
        </Reveal>
      </Side>
    </Grid>
  </Section>
  );
};

export default About;
